<template>
  <InputSelectWithCheckboxes
    v-if="integration.organisationTeamIds"
    v-model="handleOrganisationTeamIds"
    :disable-options="allSelected"
    :disabled="disabled"
    :min-selected="1"
    ok-button-label="Save"
    :options="organisationTeamOptions"
    :placeholder-text="placeholderText"
    searchable
    :selected-text="selectedText"
    show-ok-button
    show-select-all-option
    @confirm="hasConfirmed"
  >
    <template #afterSearchInput>
      <div @click.stop="toggleAllSelected">
        <div
          class="list-option row full-width bottom-border-divider cursor-pointer items-center"
        >
          <div class="text-label">
            {{ $t("social_engage_account_teams.visible_to_all_teams") }}
          </div>

          <div
            class="checkbox-left"
            :class="{
              'q-mr-md': hasScrollBar,
            }"
          >
            <Checkbox
              v-model="allSelected"
              new-design
              @update:model-value="toggleAllSelected"
            />
          </div>
        </div>
      </div>
    </template>
  </InputSelectWithCheckboxes>
</template>

<script>
import useTeamPicker from "shared/composables/useTeamPicker";

const ALL_SELECTED_VALUE = 0;

const NUMBER_OF_ITEMS_WITHOUT_SCROLLBAR = 8;

import InputSelectWithCheckboxes from "shared/components/core/forms/InputSelectWithCheckboxes.vue";

export default {
  name: "SocialEngageAccountTeams",
  components: {
    InputSelectWithCheckboxes,
  },
  props: {
    integration: {
      type: Object,
      required: true,
    },
    organisationTeams: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
  },
  emits: ["update:integration"],
  setup() {
    const { selectedTeamPreference } = useTeamPicker();

    return { selectedTeamPreference };
  },
  data() {
    return {
      confirmed: false,
      allSelected: false,
      localSelected: [],
    };
  },
  computed: {
    handleOrganisationTeamIds: {
      get() {
        return this.integration.organisationTeamIds;
      },
      set(value) {
        this.integration.organisationTeamIds = value;

        this.updateIntegration();
      },
    },
    organisationTeamOptions() {
      return this.organisationTeams.map((team) => ({
        value: team.id,
        label: team.name,
      }));
    },
    placeholderText() {
      return this.allSelected
        ? this.$t("social_engage_account_teams.all_teams")
        : this.$t("social_engage_account_teams.select_teams");
    },
    hasScrollBar() {
      return (
        this.organisationTeamOptions.length > NUMBER_OF_ITEMS_WITHOUT_SCROLLBAR
      );
    },
  },
  watch: {
    confirmed: {
      handler(value) {
        if (value) {
          this.handleBulkSelect();
        }
      },
    },
  },
  mounted() {
    if (!this.integration.organisationTeamIds) {
      if (this.$features.has("has_team_workspaces")) {
        this.integration.organisationTeamIds = [this.selectedTeamPreference];
      } else {
        this.integration.organisationTeamIds = [ALL_SELECTED_VALUE];
      }
    }

    this.allSelected =
      this.integration.organisationTeamIds.includes(ALL_SELECTED_VALUE);

    this.localSelected = this.integration.organisationTeamIds;

    this.updateOrganisationTeamIds();
    this.updateLocalSelected();
  },
  methods: {
    hasConfirmed() {
      this.confirmed = true;
    },
    async updateIntegration() {
      try {
        if (this.integration.id) {
          await this.integration.save();
          this.$emit("update:integration", this.integration);
        }
      } catch (error) {
        this.$error(
          this.$t("social_engage_account_teams.error_updating_integration"),
          error
        );
      }
    },
    handleBulkSelect() {
      if (this.allSelected) {
        this.handleOrganisationTeamIds = [
          ALL_SELECTED_VALUE,
          ...this.integration.organisationTeamIds,
        ];
      } else {
        this.handleOrganisationTeamIds =
          this.integration.organisationTeamIds.filter(
            (teamId) => teamId !== ALL_SELECTED_VALUE
          );
      }

      this.confirmed = false;
    },
    toggleAllSelected() {
      this.allSelected = !this.allSelected;

      this.updateLocalSelected();
    },
    selectedText() {
      if (this.allSelected)
        return this.$t("social_engage_account_teams.all_teams");

      const numberOfTeams = this.integration.organisationTeamIds.filter(
        (teamId) => teamId !== ALL_SELECTED_VALUE
      ).length;

      return this.$tc(
        "social_engage_account_teams.teams_count",
        numberOfTeams,
        { count: numberOfTeams }
      );
    },
    updateOrganisationTeamIds() {
      const organisationTeamOptions = this.organisationTeamOptions.map(
        (option) => option.value
      );

      this.integration.organisationTeamIds =
        this.integration.organisationTeamIds.filter(
          (teamId) =>
            organisationTeamOptions.includes(teamId) ||
            teamId === ALL_SELECTED_VALUE
        );
    },
    updateLocalSelected() {
      if (this.allSelected) {
        this.localSelected = this.integration.organisationTeamIds;

        this.integration.organisationTeamIds = [
          ...this.organisationTeamOptions.map((option) => option.value),
          ALL_SELECTED_VALUE,
        ];
      } else {
        this.integration.organisationTeamIds = this.localSelected;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.checkbox-left {
  margin-left: auto;
}

.bottom-border-divider {
  border-bottom: 1px solid $hover-background;
}
</style>
